<template>
  <!-- LED bar view takes over the full viewport when ?led is present -->
  <LedView v-if="isLedView" />
  <div v-else class="bg-white dark:bg-gray-900 flex-grow">
    <div
      v-if="eventPageStore.isLoading"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black"
    >
      <div class="text-center">
        <i class="pi pi-spin pi-spinner text-4xl cs-spinner"></i>
        <p class="mt-4 text-gray-400">{{ $t("event.loading") }}</p>
      </div>
    </div>

    <div v-else-if="error" class="flex items-center justify-center h-screen">
      <div class="text-center">
        <i class="pi pi-exclamation-triangle text-4xl text-red-500 mb-4"></i>
        <p class="text-red-600 dark:text-red-400 text-lg">{{ error }}</p>
        <router-link to="/" class="mt-4 inline-block cs-accent-link">
          {{ $t("event.backToHome") }}
        </router-link>
      </div>
    </div>

    <div v-else-if="event">
      <!-- Compact header for band view - same background, reduced font/padding -->
      <div
        v-if="isBandView"
        class="relative border-b border-gray-200 dark:border-gray-800 overflow-hidden"
      >
        <!-- Light theme background -->
        <div
          class="absolute inset-0 bg-no-repeat bg-cover bg-center opacity-100 dark:opacity-0 transition-opacity duration-300"
          style="
            background-image: url(&quot;/background/jbc-bg-light-stripe.webp&quot;);
            background-position-y: 0%;
          "
        ></div>
        <!-- Dark theme background -->
        <div
          class="absolute inset-0 bg-no-repeat bg-cover bg-center opacity-0 dark:opacity-100 transition-opacity duration-300"
          style="
            background-image: url(&quot;/background/jb-bg-dark-stripe.webp&quot;);
            background-position-y: 100%;
          "
        ></div>
        <!-- Content overlay -->
        <div class="relative">
          <div class="container mx-auto px-4 py-4">
            <h1 class="event-name text-xl font-bold text-gray-900 dark:text-white">
              {{ event.name }}
            </h1>
          </div>
        </div>
      </div>
      <!-- Full header for other views (hidden for Wise styling and phone overlay) -->
      <EventHeader v-else-if="!showPhoneOverlay && !concertsStyleStore.isWise" :event="event" />

      <div v-if="showPhoneOverlay" class="flex min-h-[100dvh] flex-col">
        <OverlayContent
          class="flex-1"
          :content="phoneOverlayHtml"
          :padded="activeOverlayTemplate.padded !== false"
          :vertical-align="activeOverlayTemplate.verticalAlign || 'top'"
          :background="activeOverlayTemplate.background || 'dark'"
        />
      </div>

      <!-- Voting Status Banner (only show when status matches) -->
      <div
        v-else-if="
          votingStatus === 'not_started' ||
          votingStatus === 'view_only' ||
          votingStatus === 'disabled'
        "
        class="container mx-auto px-4 pt-6"
      >
        <div
          v-if="votingStatus === 'not_started'"
          class="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 rounded-lg p-5 mb-6 max-w-4xl mx-auto"
        >
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0">
              <div
                class="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center"
              >
                <i class="pi pi-clock text-yellow-600 dark:text-yellow-400 text-xl"></i>
              </div>
            </div>
            <div class="flex-1">
              <h3 class="font-semibold text-yellow-900 dark:text-yellow-200 mb-1 text-lg">
                {{ $t("event.votingNotStarted") }}
              </h3>
              <p class="text-sm text-yellow-700 dark:text-yellow-300">
                {{ $t("event.votingStartsAt", { time: formatDateTime(votingStartTime) }) }}
              </p>
            </div>
          </div>
        </div>

        <div
          v-else-if="votingStatus === 'view_only'"
          class="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded-lg p-5 mb-6 max-w-4xl mx-auto"
        >
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0">
              <div
                class="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center"
              >
                <i class="pi pi-eye text-blue-600 dark:text-blue-400 text-xl"></i>
              </div>
            </div>
            <div class="flex-1">
              <h3 class="font-semibold text-blue-900 dark:text-blue-200 mb-1 text-lg">
                {{ $t("event.votingEnded") }}
              </h3>
              <p class="text-sm text-blue-700 dark:text-blue-300">
                {{
                  $t("event.resultsAvailableUntil", { time: formatDateTime(resultsAvailableUntil) })
                }}
              </p>
            </div>
          </div>
        </div>

        <div
          v-else-if="votingStatus === 'disabled'"
          class="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-lg p-5 mb-6 max-w-4xl mx-auto"
        >
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0">
              <div
                class="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center"
              >
                <i class="pi pi-stop-circle text-red-600 dark:text-red-400 text-xl"></i>
              </div>
            </div>
            <div class="flex-1">
              <h3 class="font-semibold text-red-900 dark:text-red-200 mb-1 text-lg">
                {{ $t("event.votingDisabled") }}
              </h3>
              <p class="text-sm text-red-700 dark:text-red-300">
                {{ $t("event.votingDisabledByAdmin") }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Show SleepingCat when event is ended -->
      <div
        v-else-if="votingStatus === 'ended'"
        class="flex flex-col justify-center items-center py-16"
        style="min-height: calc(100vh - 300px)"
      >
        <SleepingCat />
      </div>

      <!-- Voting/Results Content (only show if event is not ended) -->
      <div v-else>
        <!-- Band view layout: same as screen view but without QR code -->
        <div v-if="isBandView" class="container mx-auto px-4 py-8">
          <!-- Currently playing song (concert only) -->
          <div
            v-if="latestPerformedSong && !isKaraoke"
            class="bg-white dark:bg-gray-800 rounded-xl border-2 p-3 sm:p-4 md:p-6 hover:shadow-md transition-all mb-4 border-green-500 dark:border-green-600 bg-green-50/30 dark:bg-green-900/20 ring-2 ring-green-400 dark:ring-green-500"
          >
            <div class="flex items-center gap-6">
              <div class="flex-shrink-0">
                <div
                  class="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-xl text-white bg-green-500 dark:bg-green-600 animate-pulse"
                  :title="$t('event.currentlyPlaying')"
                >
                  <i class="pi pi-volume-up text-2xl"></i>
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                    {{ latestPerformedSong.title }}
                  </h3>
                </div>
                <p
                  v-if="latestPerformedSong.author"
                  class="text-gray-600 dark:text-gray-400 text-sm"
                >
                  {{ latestPerformedSong.author }}
                </p>
              </div>
              <div class="current-song-badge">
                <span
                  class="inline-block px-3 py-1 text-xs bg-green-500 text-white rounded-full font-semibold uppercase"
                >
                  {{ $t("event.currentlyPlayingBadge") }}
                </span>
              </div>
            </div>
          </div>

          <!-- Up Next Song (concert only) -->
          <div
            v-if="upNextSong && !isKaraoke"
            class="bg-white dark:bg-gray-800 rounded-xl border-1 p-3 sm:p-4 md:p-6 transition-all mb-4 border-amber-400 dark:border-amber-500 bg-amber-50/30 dark:bg-amber-900/20 ring-2 ring-amber-300 dark:ring-amber-500"
          >
            <div class="flex items-center gap-6">
              <div class="flex-shrink-0">
                <div
                  class="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-xl text-white bg-amber-500 dark:bg-amber-600"
                  :title="$t('event.upNext') || 'Up Next'"
                >
                  <i class="pi pi-forward text-2xl"></i>
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                    {{ upNextSong.title }}
                  </h3>
                </div>
                <p v-if="upNextSong.author" class="text-gray-600 dark:text-gray-400 text-sm">
                  {{ upNextSong.author }}
                </p>
              </div>
              <div class="up-next-badge flex-shrink-0 text-center">
                <span
                  class="inline-block px-3 py-1 text-xs bg-amber-500 text-white rounded-full font-semibold uppercase"
                >
                  {{ $t("event.upNext") }}
                </span>
              </div>
            </div>
          </div>

          <!-- Karaoke queue (band view) -->
          <div v-if="isKaraoke" class="space-y-3">
            <div
              v-if="!karaokeNowPlaying && karaokeQueueEntries.length === 0"
              class="text-center py-16"
            >
              <p class="text-gray-600 dark:text-gray-400 text-lg">
                {{ $t("event.karaokeQueueEmpty") }}
              </p>
            </div>
            <KaraokeQueueRow
              v-if="karaokeNowPlaying"
              :entry="karaokeNowPlaying"
              :is-now-playing="true"
            />
            <KaraokeQueueRow
              v-for="(entry, index) in karaokeQueueEntries"
              :key="entry.voteId"
              :entry="entry"
              :queue-number="index + 1"
            />
          </div>

          <!-- Empty state (concert only) -->
          <div
            v-else-if="sortedResultsFiltered.length === 0 && !upNextSong && !latestPerformedSong"
            class="text-center py-16"
          >
            <div
              class="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <i class="pi pi-chart-bar text-3xl text-gray-400"></i>
            </div>
            <p class="text-gray-600 dark:text-gray-400 text-lg">{{ $t("event.noVotesYet") }}</p>
          </div>

          <!-- Song results list (concert only) -->
          <div v-else-if="!isKaraoke" class="space-y-3">
            <template v-for="(song, index) in sortedResultsFiltered" :key="song.id">
              <SetEndDivider v-if="index === setEndLineIndex" :label="$t('event.setEndLine')" />
              <div
                class="bg-white dark:bg-gray-800 rounded-xl border-2 p-3 sm:p-4 md:p-6 hover:shadow-md transition-all"
                :class="getResultCardClasses(song, index)"
              >
                <div class="flex items-center gap-3 sm:gap-6">
                  <div class="flex-shrink-0">
                    <div
                      v-if="song.id === latestPerformedSong?.id"
                      class="w-10 h-10 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-sm sm:text-xl text-white bg-green-500 dark:bg-green-600 animate-pulse"
                      :title="$t('event.currentlyPlaying')"
                    >
                      <i class="pi pi-volume-up text-base sm:text-2xl"></i>
                    </div>
                    <div
                      v-else
                      class="w-10 h-10 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-sm sm:text-xl text-white"
                      :class="getRankBadgeClasses(song, index)"
                    >
                      {{ index + 1 }}
                    </div>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-0.5 sm:mb-1">
                      <h3
                        class="text-base sm:text-xl font-semibold text-gray-900 dark:text-white truncate"
                      >
                        {{ song.title }}
                      </h3>
                      <PinnedSongMark :pinned="song.pinned" />
                      <i
                        v-if="song.id === latestPerformedSong?.id"
                        class="pi pi-volume-up text-green-600 dark:text-green-400 animate-pulse text-sm sm:text-base"
                        :title="$t('event.currentlyPlaying')"
                      ></i>
                    </div>
                    <p
                      v-if="song.author"
                      class="text-gray-600 dark:text-gray-400 text-xs sm:text-sm truncate"
                    >
                      {{ song.author }}
                    </p>
                  </div>
                  <!-- Vote count only (band view is display-only) -->
                  <SongVoteControls
                    size="lg"
                    :vote-count="getVoteCount(song.id)"
                    :show-vote-button="false"
                  />
                </div>
              </div>
            </template>
            <SetEndDivider
              v-if="setEndLineIndex >= sortedResultsFiltered.length"
              :label="$t('event.setEndLine')"
            />
          </div>
        </div>

        <!-- Screen layout: default QR right/top; ?qr=left swaps to QR left/bottom -->
        <div v-else-if="isScreenView" class="container mx-auto px-4 py-8">
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <!-- QR Code -->
            <div
              :class="[
                'lg:col-span-1',
                isScreenQrLeft ? 'order-2 lg:order-1' : 'order-1 lg:order-2'
              ]"
            >
              <div
                class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm p-6 flex flex-col items-center"
              >
                <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-2 text-center">
                  {{ $t("event.joinVoting") }}
                </h2>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center">
                  {{ $t("event.joinVotingDescription") }}
                </p>
                <div v-if="qrCodeDataUrl" class="bg-white p-4 rounded-xl border border-gray-200">
                  <img
                    :src="qrCodeDataUrl"
                    :alt="$t('event.qrCodeAlt')"
                    class="w-full max-w-xs mx-auto"
                  />
                </div>
                <div
                  v-if="event?.eventCode"
                  class="mt-4 px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-center"
                >
                  <div
                    class="text-2xl font-mono font-bold text-gray-900 dark:text-white tracking-widest"
                  >
                    {{ event.eventCode }}
                  </div>
                </div>
                <p
                  v-if="eventUrl"
                  class="mt-4 text-xs text-gray-500 dark:text-gray-400 break-all text-center"
                >
                  {{ eventUrl }}
                </p>
              </div>
            </div>

            <!-- Results list -->
            <div
              :class="[
                'lg:col-span-2',
                isScreenQrLeft ? 'order-1 lg:order-2' : 'order-2 lg:order-1'
              ]"
            >
              <!-- Currently playing song (concert screen view only) -->
              <div
                v-if="latestPerformedSong && !isKaraoke"
                class="bg-white dark:bg-gray-800 rounded-xl border-2 p-3 sm:p-4 md:p-6 hover:shadow-md transition-all mb-4 border-green-500 dark:border-green-600 bg-green-50/30 dark:bg-green-900/20 ring-2 ring-green-400 dark:ring-green-500"
              >
                <div class="flex items-center gap-6">
                  <!-- Currently Performing Badge -->
                  <div class="flex-shrink-0">
                    <div
                      class="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-xl text-white bg-green-500 dark:bg-green-600 animate-pulse"
                      :title="$t('event.currentlyPlaying')"
                    >
                      <i class="pi pi-volume-up text-2xl"></i>
                    </div>
                  </div>

                  <!-- Song Info -->
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                        {{ latestPerformedSong.title }}
                      </h3>
                    </div>
                    <p
                      v-if="latestPerformedSong.author"
                      class="text-gray-600 dark:text-gray-400 text-sm"
                    >
                      {{ latestPerformedSong.author }}
                    </p>
                  </div>

                  <div class="current-song-badge">
                    <span
                      class="inline-block px-3 py-1 text-xs bg-green-500 text-white rounded-full font-semibold uppercase"
                    >
                      {{ $t("event.currentlyPlayingBadge") }}
                    </span>
                  </div>

                  <!-- Vote Count (hidden for currently playing) -->
                </div>
              </div>

              <!-- Up Next Song (concert screen view only) -->
              <div
                v-if="upNextSong && !isKaraoke"
                class="bg-white dark:bg-gray-800 rounded-xl border-1 p-3 sm:p-4 md:p-6 transition-all mb-4 border-amber-400 dark:border-amber-500 bg-amber-50/30 dark:bg-amber-900/20 ring-2 ring-amber-300 dark:ring-amber-500"
              >
                <div class="flex items-center gap-6">
                  <!-- Up Next Badge -->
                  <div class="flex-shrink-0">
                    <div
                      class="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-xl text-white bg-amber-500 dark:bg-amber-600"
                      :title="$t('event.upNext') || 'Up Next'"
                    >
                      <i class="pi pi-forward text-2xl"></i>
                    </div>
                  </div>

                  <!-- Song Info -->
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                        {{ upNextSong.title }}
                      </h3>
                    </div>
                    <p v-if="upNextSong.author" class="text-gray-600 dark:text-gray-400 text-sm">
                      {{ upNextSong.author }}
                    </p>
                  </div>

                  <!-- Up Next Badge instead of vote count -->
                  <div class="up-next-badge flex-shrink-0 text-center">
                    <span
                      class="inline-block px-3 py-1 text-xs bg-amber-500 text-white rounded-full font-semibold uppercase"
                    >
                      {{ $t("event.upNext") }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Karaoke queue (screen view) -->
              <div v-if="isKaraoke" class="space-y-3">
                <div
                  v-if="!karaokeNowPlaying && karaokeQueueEntries.length === 0"
                  class="text-center py-16"
                >
                  <p class="text-gray-600 dark:text-gray-400 text-lg">
                    {{ $t("event.karaokeQueueEmpty") }}
                  </p>
                </div>
                <KaraokeQueueRow
                  v-if="karaokeNowPlaying"
                  :entry="karaokeNowPlaying"
                  :is-now-playing="true"
                />
                <KaraokeQueueRow
                  v-for="(entry, index) in karaokeQueueEntries"
                  :key="entry.voteId"
                  :entry="entry"
                  :queue-number="index + 1"
                />
              </div>

              <!-- Empty state (concert only) -->
              <div
                v-else-if="sortedResultsFiltered.length === 0 && !upNextSong"
                class="text-center py-16"
              >
                <div
                  class="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <i class="pi pi-chart-bar text-3xl text-gray-400"></i>
                </div>
                <p class="text-gray-600 dark:text-gray-400 text-lg">{{ $t("event.noVotesYet") }}</p>
              </div>

              <!-- Concert vote results -->
              <div v-else-if="!isKaraoke" class="space-y-3">
                <template v-for="(song, index) in sortedResultsFiltered" :key="song.id">
                  <SetEndDivider v-if="index === setEndLineIndex" :label="$t('event.setEndLine')" />
                  <div
                    class="bg-white dark:bg-gray-800 rounded-xl border-2 p-3 sm:p-4 md:p-6 hover:shadow-md transition-all"
                    :class="getResultCardClasses(song, index)"
                  >
                    <div class="flex items-center gap-3 sm:gap-6">
                      <!-- Rank Badge / Currently Performing Badge -->
                      <div class="flex-shrink-0">
                        <div
                          v-if="song.id === latestPerformedSong?.id"
                          class="w-10 h-10 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-sm sm:text-xl text-white bg-green-500 dark:bg-green-600 animate-pulse"
                          :title="$t('event.currentlyPlaying')"
                        >
                          <i class="pi pi-volume-up text-base sm:text-2xl"></i>
                        </div>
                        <div
                          v-else
                          class="w-10 h-10 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-sm sm:text-xl text-white"
                          :class="getRankBadgeClasses(song, index)"
                        >
                          {{ index + 1 }}
                        </div>
                      </div>

                      <!-- Song Info -->
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2 mb-0.5 sm:mb-1">
                          <h3
                            class="text-base sm:text-xl font-semibold text-gray-900 dark:text-white truncate"
                          >
                            {{ song.title }}
                          </h3>
                          <PinnedSongMark :pinned="song.pinned" />
                          <i
                            v-if="song.id === latestPerformedSong?.id"
                            class="pi pi-volume-up text-green-600 dark:text-green-400 animate-pulse text-sm sm:text-base"
                            :title="$t('event.currentlyPlaying')"
                          ></i>
                        </div>
                        <p
                          v-if="song.author"
                          class="text-gray-600 dark:text-gray-400 text-xs sm:text-sm truncate"
                        >
                          {{ song.author }}
                        </p>
                      </div>

                      <!-- Vote Count -->
                      <SongVoteControls
                        size="lg"
                        :vote-count="getVoteCount(song.id)"
                        :show-vote-button="false"
                      />
                    </div>
                  </div>
                </template>
                <SetEndDivider
                  v-if="setEndLineIndex >= sortedResultsFiltered.length"
                  :label="$t('event.setEndLine')"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Default interactive layout -->
        <div v-else class="container mx-auto px-4 py-8">
          <!-- View Toggle Tabs -->
          <div
            v-if="songs.length > 0"
            class="mb-6 flex gap-2 border-b border-gray-200 dark:border-gray-700"
          >
            <button
              :class="[
                'px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px',
                activeView === 'vote'
                  ? 'cs-tab-active border-b-2 -mb-px'
                  : 'text-gray-500 dark:text-gray-400 border-transparent border-b-2 -mb-px hover:text-gray-700 dark:hover:text-gray-300'
              ]"
              @click="activeView = 'vote'"
            >
              <i class="pi pi-thumbs-up mr-2"></i>
              {{ primaryTabLabel }}
            </button>
            <button
              :class="[
                'px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px',
                activeView === 'results'
                  ? 'cs-tab-active border-b-2 -mb-px'
                  : 'text-gray-500 dark:text-gray-400 border-transparent border-b-2 -mb-px hover:text-gray-700 dark:hover:text-gray-300'
              ]"
              @click="activeView = 'results'"
            >
              <i class="pi pi-chart-bar mr-2"></i>
              {{ secondaryTabLabel }}
            </button>
          </div>

          <!-- Voting View -->
          <div v-if="activeView === 'vote'">
            <!-- Filters Section -->
            <div
              v-if="songs.length > 0"
              class="mb-8 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm"
            >
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- Search/Title Filter -->
                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{
                    $t("event.search")
                  }}</label>
                  <IconField icon-position="left" class="w-full">
                    <InputIcon class="pi pi-search text-gray-400 dark:text-gray-500" />
                    <InputText
                      v-model="filters.title"
                      :placeholder="$t('event.searchPlaceholder')"
                      class="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </IconField>
                </div>

                <!-- Genre Filter -->
                <!-- <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Žanr</label>
              <Select
                v-model="filters.genre"
                :options="genreOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Kõik žanrid"
                class="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-lg shadow-sm"
                :showClear="true"
              />
            </div> -->

                <!-- Category Filter -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{
                    $t("event.category")
                  }}</label>
                  <Select
                    v-model="filters.category"
                    :options="categoryOptions"
                    option-label="label"
                    option-value="value"
                    :placeholder="$t('event.allCategories')"
                    class="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-lg shadow-sm"
                    :show-clear="true"
                  />
                </div>

                <!-- Sort by Votes -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{
                    $t("event.sort")
                  }}</label>
                  <Select
                    v-model="filters.sortBy"
                    :options="sortOptions"
                    option-label="label"
                    option-value="value"
                    :placeholder="$t('event.sortPlaceholder')"
                    class="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-lg shadow-sm"
                  />
                </div>
              </div>
            </div>

            <div v-if="filteredSongs.length === 0" class="text-center py-16">
              <div
                class="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <i class="pi pi-filter text-3xl text-gray-400"></i>
              </div>
              <p class="text-gray-600 dark:text-gray-400 text-lg mb-4">
                {{ $t("event.noSongsMatch") }}
              </p>
              <button class="cs-accent-link font-medium transition-colors" @click="clearFilters">
                {{ $t("event.clearFilters") }}
              </button>
            </div>

            <div v-else-if="songs.length === 0" class="text-center py-16">
              <div
                class="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <i class="pi pi-check-square text-3xl text-gray-400"></i>
              </div>
              <p class="text-gray-600 dark:text-gray-400 text-lg">
                {{ $t("event.noSongsAvailable") }}
              </p>
            </div>

            <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <SongCard
                v-for="song in filteredSongs"
                :key="song.id"
                :song="song"
                :event-id="event.id"
                :vote-count="getVoteCount(song.id)"
                :is-performed="song.isPerformed"
                :is-up-next="upNextSong?.id === song.id"
                :can-vote="canVote && upNextSong?.id !== song.id"
                :has-voted="hasUserVoted(song.id)"
                :is-voting="votingInProgress && votingSongId === song.id"
                :is-vote-locked="votingInProgress"
                :show-vote-count="!isKaraoke"
                :show-vote-button="!isKaraoke"
                :show-karaoke-indicator="isKaraoke && event.signUpMode !== 'none'"
                :is-karaoke-queued="isSongQueuedInKaraoke(song.id)"
                :clickable="isKaraoke && event.signUpMode !== 'none'"
                @click="handleSongClick(song)"
                @vote="handleVote"
              />
            </div>
          </div>

          <!-- Results View -->
          <div v-else-if="activeView === 'results'">
            <div v-if="isKaraoke" class="space-y-3">
              <div
                v-if="!karaokeNowPlaying && karaokeQueueEntries.length === 0"
                class="text-center py-16"
              >
                <p class="text-gray-600 dark:text-gray-400 text-lg">
                  {{ $t("event.karaokeQueueEmpty") }}
                </p>
              </div>
              <KaraokeQueueRow
                v-if="karaokeNowPlaying"
                :entry="karaokeNowPlaying"
                :is-now-playing="true"
              />
              <KaraokeQueueRow
                v-for="(entry, index) in karaokeQueueEntries"
                :key="entry.voteId"
                :entry="entry"
                :queue-number="index + 1"
              />
            </div>

            <template v-else>
              <div v-if="sortedResults.length === 0 && !upNextSong" class="text-center py-16">
                <div
                  class="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <i class="pi pi-chart-bar text-3xl text-gray-400"></i>
                </div>
                <p class="text-gray-600 dark:text-gray-400 text-lg">{{ $t("event.noVotesYet") }}</p>
              </div>

              <div v-else class="space-y-3">
                <!-- Up Next Song (shown as song 0 at the top) -->
                <div
                  v-if="upNextSong"
                  class="bg-white dark:bg-gray-800 rounded-xl border-2 p-3 sm:p-4 md:p-6 hover:shadow-md transition-shadow border-amber-400 dark:border-amber-500 bg-amber-50/30 dark:bg-amber-900/20 ring-2 ring-amber-300 dark:ring-amber-500"
                >
                  <div class="flex items-center gap-2 sm:gap-4 md:gap-6">
                    <div class="flex-shrink-0">
                      <div
                        class="w-10 h-10 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center font-bold text-sm sm:text-lg md:text-xl text-white bg-amber-500 dark:bg-amber-600"
                      >
                        <i class="pi pi-forward text-base sm:text-xl md:text-2xl"></i>
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-0.5 sm:mb-1">
                        <h3
                          class="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white truncate"
                        >
                          {{ upNextSong.title }}
                        </h3>
                      </div>
                      <p
                        v-if="upNextSong.author"
                        class="text-gray-600 dark:text-gray-400 text-xs sm:text-sm truncate"
                      >
                        {{ upNextSong.author }}
                      </p>
                    </div>
                    <div class="up-next-badge flex-shrink-0 text-center">
                      <span
                        class="inline-block px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs bg-amber-500 text-white rounded-full font-semibold uppercase"
                      >
                        {{ $t("event.upNext") }}
                      </span>
                    </div>
                  </div>
                </div>

                <TransitionGroup name="results-song" tag="div" class="space-y-3">
                  <div v-for="item in resultsListItems" :key="item.id" class="results-song-item">
                    <SetEndDivider v-if="item.type === 'divider'" :label="$t('event.setEndLine')" />
                    <div
                      v-else
                      class="bg-white dark:bg-gray-800 rounded-xl border-2 p-3 sm:p-4 md:p-6 hover:shadow-md transition-shadow"
                      :class="[
                        getResultCardClasses(item.song, item.index),
                        item.song.id === recentlyVotedSongId ? 'results-song-voted' : ''
                      ]"
                    >
                      <div class="flex items-center gap-2 sm:gap-4 md:gap-6">
                        <!-- Rank Badge / Currently Performing Badge -->
                        <div class="flex-shrink-0">
                          <div
                            v-if="item.song.id === latestPerformedSong?.id"
                            class="w-10 h-10 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center font-bold text-sm sm:text-lg md:text-xl text-white bg-green-500 dark:bg-green-600 animate-pulse"
                            :title="$t('event.currentlyPlaying')"
                          >
                            <i class="pi pi-volume-up text-base sm:text-xl md:text-2xl"></i>
                          </div>
                          <div
                            v-else
                            class="w-10 h-10 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center font-bold text-sm sm:text-lg md:text-xl text-white"
                            :class="getRankBadgeClasses(item.song, item.index)"
                          >
                            {{ item.index + 1 }}
                          </div>
                        </div>

                        <!-- Song Info -->
                        <div class="flex-1 min-w-0">
                          <div class="flex items-center gap-2 mb-0.5 sm:mb-1">
                            <h3
                              class="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white truncate"
                            >
                              {{ item.song.title }}
                            </h3>
                            <i
                              v-if="item.song.id === latestPerformedSong?.id"
                              class="pi pi-volume-up text-green-600 dark:text-green-400 animate-pulse text-sm sm:text-base"
                              :title="$t('event.currentlyPlaying')"
                            ></i>
                          </div>
                          <p
                            v-if="item.song.author"
                            class="text-gray-600 dark:text-gray-400 text-xs sm:text-sm truncate"
                          >
                            {{ item.song.author }}
                          </p>
                          <div v-if="item.song.id === latestPerformedSong?.id" class="mb-1 sm:mb-2">
                            <span
                              class="inline-block px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs bg-green-500 text-white rounded-full font-semibold"
                            >
                              {{ $t("event.currentlyPlaying").toUpperCase() }}
                            </span>
                          </div>
                        </div>

                        <PinnedSongMark
                          v-if="item.song.pinned"
                          pinned
                          :label="$t('event.pinned')"
                        />
                        <SongVoteControls
                          v-else
                          size="lg"
                          :vote-count="getVoteCount(item.song.id)"
                          :can-vote="canVote && !isKaraoke"
                          :has-voted="hasUserVoted(item.song.id)"
                          :is-voting="votingInProgress && votingSongId === item.song.id"
                          :is-vote-locked="votingInProgress"
                          @vote="handleVote(item.song.id)"
                        />
                      </div>
                    </div>
                  </div>
                </TransitionGroup>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Karaoke Sign-Up Modals (direct & qr modes) -->
    <KaraokeSignUpModal
      v-if="isKaraoke && event?.signUpMode === 'direct'"
      v-model:visible="showInstructionsModal"
      :song="selectedSongForModal"
      :is-already-queued="isSongQueuedInKaraoke(selectedSongForModal?.id)"
      @close="handleInstructionsClose"
    />

    <KaraokeNameInputModal
      v-if="isKaraoke && event?.signUpMode === 'qr'"
      :visible="showQRModal"
      :song="selectedSongForModal"
      :event-id="event.id"
      :is-already-queued="isSongQueuedInKaraoke(selectedSongForModal?.id)"
      @update:visible="handleQRModalClose"
      @qr-generated="handleQRGenerated"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import QRCode from "qrcode";
import { useI18n } from "vue-i18n";
import api from "../services/api";
import { useVotesStore } from "../stores/votes";
import { useSocketStore } from "../stores/socket";
import { useDeviceStore } from "../stores/device";
import { useThemeStore } from "../stores/theme";
import { useConcertsStyleStore } from "../stores/concertsStyle";
import { useEventPageStore } from "../stores/eventPage";
import { useLanguageStore } from "../stores/language";
import { resolveEventLocales, resolveLandingLocale } from "../constants/locales";
import { buildEventPublicUrl } from "../utils/eventUrl";
import { formatDateTime } from "../utils/dateFormat";
import { useLiveSetEndLine } from "../composables/useSetEndSlots";
import { filterSongsForEvent } from "../utils/eventSongFilter";
import { includeVoteInRankedPlaylist } from "../utils/rankedPlaylist";
import PinnedSongMark from "../components/PinnedSongMark.vue";
import { isKaraokeEvent } from "../constants/eventTypes";
import EventHeader from "../components/EventHeader.vue";
import SongCard from "../components/SongCard.vue";
import SongVoteControls from "../components/SongVoteControls.vue";
import KaraokeQueueRow from "../components/KaraokeQueueRow.vue";
import OverlayContent from "../components/OverlayContent.vue";
import SleepingCat from "../components/SleepingCat.vue";
import SetEndDivider from "../components/SetEndDivider.vue";
import LedView from "./LedView.vue";
import KaraokeSignUpModal from "../components/KaraokeSignUpModal.vue";
import KaraokeNameInputModal from "../components/KaraokeNameInputModal.vue";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import Button from "primevue/button";
import { useRealtimeSafetyNet } from "../composables/useRealtimeSafetyNet";
import { fetchEventDataBySlug } from "../utils/quietEventRefresh";
import { shouldShowPhoneOverlay } from "../utils/overlayDisplay";
import { resolveOverlayHtml } from "../utils/overlayTemplate";

const route = useRoute();
const { t } = useI18n();
const votesStore = useVotesStore();
const socketStore = useSocketStore();
const deviceStore = useDeviceStore();
const themeStore = useThemeStore();
const concertsStyleStore = useConcertsStyleStore();
const eventPageStore = useEventPageStore();
const languageStore = useLanguageStore();

const isLedView = computed(() => "led" in route.query);
if (!isLedView.value) {
  eventPageStore.setLoading(true);
}

const event = ref(null);
const songs = ref([]);
const error = ref(null);
const votingStatus = ref(null);
const votingStartTime = ref(null);
const votingEndTime = ref(null);
const eventEndTime = ref(null);
const resultsAvailableUntil = ref(null);
const activeView = ref("vote"); // 'vote' or 'results'

const votingInProgress = ref(false);
const votingSongId = ref(null);
const recentlyVotedSongId = ref(null);
let recentlyVotedTimeout = null;
const minimumVoteSpinnerMs = 1000;

// Setlist settings for "set end" marker calculation
const setlistSettings = ref(null); // { pauseTime, defaultSongTime }

// Screen-only layout flag (?screen query parameter – works with ?screen or ?screen=1)
const isScreenView = computed(() => "screen" in route.query);

// Screen layout: ?qr=left puts QR on the left (desktop) / below results (mobile)
const isScreenQrLeft = computed(() => {
  const qr = route.query.qr;
  const value = Array.isArray(qr) ? qr[0] : qr;
  return String(value || "").toLowerCase() === "left";
});

// Band view layout flag (?band query parameter – compact header + song list only)
const isBandView = computed(() => "band" in route.query);
const activeOverlayTemplate = ref(null);
const showPhoneOverlay = computed(() =>
  shouldShowPhoneOverlay({
    overlayTemplate: activeOverlayTemplate.value,
    isScreenView: isScreenView.value,
    isBandView: isBandView.value
  })
);
const phoneOverlayHtml = computed(() =>
  resolveOverlayHtml(activeOverlayTemplate.value, languageStore.locale)
);

// Check if current event is karaoke type
const isKaraoke = computed(() => isKaraokeEvent(event.value));

// State for karaoke signup modals
const showInstructionsModal = ref(false);
const showQRModal = ref(false);
const selectedSongForModal = ref(null);

const primaryTabLabel = computed(() =>
  isKaraoke.value ? t("event.songsListTab") : t("event.voteTab")
);

const secondaryTabLabel = computed(() =>
  isKaraoke.value ? t("event.queueTab") : t("event.resultsTab")
);

const karaokeNowPlaying = computed(() => {
  if (!event.value) return null;
  return votesStore.getNowPlaying(event.value.id);
});

const karaokeQueueEntries = computed(() => {
  if (!event.value) return [];
  return votesStore.getQueueEntries(event.value.id);
});

const karaokeQueuedSongIds = computed(() => {
  const ids = new Set(karaokeQueueEntries.value.map((entry) => entry.songId));
  if (karaokeNowPlaying.value?.songId) {
    ids.add(karaokeNowPlaying.value.songId);
  }
  return ids;
});

const isSongQueuedInKaraoke = (songId) => {
  if (!songId) return false;
  return karaokeQueuedSongIds.value.has(songId);
};

// Filters
const filters = ref({
  title: "",
  genre: null,
  category: null,
  sortBy: null
});

// Public event URL (used for QR code on screen view)
const eventUrl = computed(() => {
  if (!event.value?.slug) return "";
  return buildEventPublicUrl(
    event.value.slug,
    resolveEventLocales(event.value.enabledLocales, event.value.defaultLocale).defaultLocale
  );
});

const qrCodeDataUrl = ref("");

watch(
  eventUrl,
  async (url) => {
    if (!url) {
      qrCodeDataUrl.value = "";
      return;
    }
    try {
      qrCodeDataUrl.value = await QRCode.toDataURL(url, {
        width: 300,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF"
        }
      });
    } catch (err) {
      console.error("Failed to generate QR code:", err);
      qrCodeDataUrl.value = "";
    }
  },
  { immediate: true }
);

// Initialize device ID on mount
deviceStore.initDeviceId();

const canVote = computed(() => {
  return votingStatus.value === "available";
});

const getVoteCount = (songId) => {
  return votesStore.getSongVoteCount(event.value?.id, songId);
};

const getFirstVotedAt = (songId) => {
  if (!event.value) return null;
  const eventVotes = votesStore.getEventVotes(event.value.id);
  if (!eventVotes || eventVotes.length === 0) return null;

  // Match the exact same lookup pattern as getSongVoteCount in the votes store
  // The votes store only checks vote.songId, so we do the same
  const songVote = eventVotes.find((vote) => {
    // Check songId directly (primary lookup)
    if (vote.songId === songId) return true;
    // Also check nested song.id as fallback (in case structure differs)
    if (vote.song?.id === songId) return true;
    return false;
  });

  if (!songVote) {
    // Debug: log if song has votes but vote object not found
    const voteCount = getVoteCount(songId);
    if (voteCount > 0) {
      console.warn(
        `[getFirstVotedAt] Song ${songId} has ${voteCount} votes but vote object not found in eventVotes`
      );
    }
    return null;
  }

  // Return firstVotedAt if it exists
  const firstVotedAt = songVote.firstVotedAt;
  if (!firstVotedAt && getVoteCount(songId) > 0) {
    console.warn(
      `[getFirstVotedAt] Song ${songId} has votes but firstVotedAt is missing`,
      songVote
    );
  }
  return firstVotedAt || null;
};

const hasUserVoted = (songId) => {
  return votesStore.hasUserVotedForSong(event.value?.id, songId);
};

// Get performed status for a song
const getIsPerformed = (songId) => {
  if (!event.value) return false;
  const eventVotes = votesStore.getEventVotes(event.value.id);
  const songVote = eventVotes.find((vote) => vote.songId === songId);
  return songVote ? songVote.isPerformed : false;
};

// Get performed timestamp for a song
/* const getPerformedAt = (songId) => {
  if (!event.value) return null;
  const eventVotes = votesStore.getEventVotes(event.value.id);
  const songVote = eventVotes.find((vote) => vote.songId === songId);
  return songVote && songVote.isPerformed ? songVote.performedAt : null;
}; */

// Get the up-next song (locked, cannot receive votes)
const upNextSong = computed(() => {
  if (!event.value) return null;
  const upNextId = votesStore.getUpNextSongId(event.value.id);
  if (!upNextId) return null;

  // Find song details
  const song = songs.value.find((s) => s.id === upNextId);
  if (!song) return null;

  return {
    ...song,
    voteCount: getVoteCount(song.id),
    isUpNext: true
  };
});

// Get the latest performed song (the one marked most recently that hasn't ended)
const latestPerformedSong = computed(() => {
  if (!event.value) return null;

  const eventVotes = votesStore.getEventVotes(event.value.id);
  // Filter for performed songs that haven't ended (endedAt is null)
  const performedSongs = eventVotes.filter(
    (vote) => vote.isPerformed && vote.performedAt && !vote.endedAt
  );

  if (performedSongs.length === 0) return null;

  // Sort by performedAt descending (most recent first) and get the first one
  const sorted = performedSongs.sort((a, b) => {
    return new Date(b.performedAt) - new Date(a.performedAt);
  });

  const latest = sorted[0];

  // Find the song details
  const song = songs.value.find((s) => s.id === latest.songId);
  if (!song) return null;

  return {
    ...song,
    voteCount: getVoteCount(song.id),
    isPerformed: true,
    performedAt: latest.performedAt,
    songLength: song.songLength || latest.song?.songLength || null
  };
});

// Calculate the visual rank for a song (excluding currently performed song from ranking)
const getVisualRank = (song, index) => {
  // If this is the currently performed song, it doesn't have a rank
  if (song.id === latestPerformedSong.value?.id) return -1;

  // Count how many songs before this one are the currently performed song
  let offset = 0;
  for (let i = 0; i < index; i++) {
    if (sortedResults.value[i]?.id === latestPerformedSong.value?.id) {
      offset = 1;
      break;
    }
  }

  return index - offset;
};

// Get result card border/background classes based on rank
const getResultCardClasses = (song, index) => {
  // Currently being performed song - use green color
  if (song.id === latestPerformedSong.value?.id) {
    return "border-green-500 dark:border-green-600 bg-green-50/30 dark:bg-green-900/20 ring-2 ring-green-400 dark:ring-green-500";
  }

  const rank = getVisualRank(song, index);
  const rankClass = concertsStyleStore.rankCardClass(rank);
  return song.pinned ? `${rankClass} cs-pin-card` : rankClass;
};

// Get rank badge background classes based on rank
const getRankBadgeClasses = (song, index) => {
  const rank = getVisualRank(song, index);
  return concertsStyleStore.rankBadgeClass(rank);
};

// Extract unique genres and categories from songs
// Commented out because it's not used; genres are currently not used in the frontend
/* const genreOptions = computed(() => {
  const genres = new Set();
  songs.value.forEach((song) => {
    if (song.genre) genres.add(song.genre);
  });
  return Array.from(genres)
    .sort()
    .map((g) => ({ label: g, value: g }));
}); */

const categoryOptions = computed(() => {
  const categories = new Set();
  songs.value.forEach((song) => {
    if (song.categories && Array.isArray(song.categories)) {
      song.categories.forEach((cat) => categories.add(cat));
    } else if (song.category) {
      categories.add(song.category);
    }
  });
  return Array.from(categories)
    .sort()
    .map((c) => ({ label: c, value: c }));
});

const sortOptions = computed(() => [
  { label: t("event.sortOptions.mostVotes"), value: "votes_desc" },
  { label: t("event.sortOptions.leastVotes"), value: "votes_asc" },
  { label: t("event.sortOptions.titleAsc"), value: "title_asc" },
  { label: t("event.sortOptions.titleDesc"), value: "title_desc" }
]);

// Filtered and sorted songs
const filteredSongs = computed(() => {
  let result = [...songs.value];

  // Filter by title/artist
  if (filters.value.title) {
    const query = filters.value.title.toLowerCase();
    result = result.filter(
      (song) =>
        song.title?.toLowerCase().includes(query) || song.author?.toLowerCase().includes(query)
    );
  }

  // Filter by genre
  if (filters.value.genre) {
    result = result.filter((song) => song.genre === filters.value.genre);
  }

  // Filter by category
  if (filters.value.category) {
    result = result.filter((song) => {
      if (song.categories && Array.isArray(song.categories)) {
        return song.categories.includes(filters.value.category);
      }
      return song.category === filters.value.category;
    });
  }

  // Add isPerformed status and firstVotedAt to each song
  result = result.map((song) => ({
    ...song,
    isPerformed: getIsPerformed(song.id),
    firstVotedAt: getFirstVotedAt(song.id),
    isUpNext: upNextSong.value?.id === song.id
  }));

  // Sort
  if (filters.value.sortBy) {
    result.sort((a, b) => {
      const aVotes = getVoteCount(a.id);
      const bVotes = getVoteCount(b.id);

      switch (filters.value.sortBy) {
        case "votes_desc":
          if (bVotes !== aVotes) {
            return bVotes - aVotes;
          }

          // If votes are equal, sort by first vote time (ascending - earlier votes first)
          const aFirstVote = a.firstVotedAt ? new Date(a.firstVotedAt).getTime() : Infinity;
          const bFirstVote = b.firstVotedAt ? new Date(b.firstVotedAt).getTime() : Infinity;
          return aFirstVote - bFirstVote;
        case "votes_asc":
          if (aVotes !== bVotes) {
            return aVotes - bVotes;
          }

          // If votes are equal, sort by first vote time (ascending - earlier votes first)
          const aFirstVoteAsc = a.firstVotedAt ? new Date(a.firstVotedAt).getTime() : Infinity;
          const bFirstVoteAsc = b.firstVotedAt ? new Date(b.firstVotedAt).getTime() : Infinity;
          return aFirstVoteAsc - bFirstVoteAsc;
        case "title_asc":
          return (a.title || "").localeCompare(b.title || "");
        case "title_desc":
          return (b.title || "").localeCompare(a.title || "");
        default:
          return 0;
      }
    });
  }

  return result;
});

// Results view: unperformed songs in backend order. Pins stay first even with no votes.
const sortedResults = computed(() => {
  if (!event.value) return [];

  // Get the pre-sorted results from the votes store (sorted by backend)
  const eventVotes = votesStore.getEventVotes(event.value.id);

  // Pinned songs stay on the playlist with zero votes. Disabled songs are
  // already dropped by the backend; this is a safety net for a stale cache.
  const filtered = eventVotes.filter((vote) => includeVoteInRankedPlaylist(vote, songs.value));

  // Map to include song details from our local songs array for additional info if needed
  return filtered.map((vote) => {
    const songId = vote.songId || vote.song?.id;
    const localSong = songs.value.find((s) => s.id === songId);
    return {
      id: songId,
      title: vote.song?.title || localSong?.title || "Unknown",
      author: vote.song?.author || localSong?.author || "",
      originalArtist: vote.song?.originalArtist || localSong?.originalArtist || "",
      genre: vote.song?.genre || localSong?.genre || "",
      category: vote.song?.category || localSong?.category || "",
      songLength: vote.song?.songLength || localSong?.songLength || null,
      voteCount: vote.totalVotes,
      pinned: vote.pinned === true,
      isPerformed: vote.isPerformed || false,
      firstVotedAt: vote.firstVotedAt,
      performedAt: vote.performedAt,
      endedAt: vote.endedAt
    };
  });
});

// Results filtered to exclude the up-next song (shown separately at position 0)
const sortedResultsFiltered = computed(() => {
  if (!upNextSong.value) return sortedResults.value;
  return sortedResults.value.filter((song) => song.id !== upNextSong.value.id);
});

// Flat list merging song rows and the set-end divider for TransitionGroup
const resultsListItems = computed(() => {
  const items = [];
  const dividerIdx = setEndLineIndex.value;

  sortedResultsFiltered.value.forEach((song, index) => {
    if (index === dividerIdx) {
      items.push({ type: "divider", id: "__set-end-divider__" });
    }
    items.push({ type: "song", id: song.id, song, index });
  });
  if (dividerIdx >= sortedResultsFiltered.value.length) {
    items.push({ type: "divider", id: "__set-end-divider__" });
  }

  return items;
});

// ── Setlist "Set End" line computation ──────────────────────────────────────
const {
  lineIndex: setEndLineIndex,
  start: startSetEndSlots,
  stop: stopSetEndSlots
} = useLiveSetEndLine(
  computed(() => ({
    event: event.value,
    rankedSongs: sortedResultsFiltered.value,
    upNextSong: upNextSong.value,
    currentSong: latestPerformedSong.value,
    pauseTimeSec: setlistSettings.value?.pauseTime || 30,
    defaultSongTimeSec: setlistSettings.value?.defaultSongTime || 240,
    settingsReady: !!setlistSettings.value
  })),
  { intervalMs: 5000 }
);

const clearFilters = () => {
  filters.value = {
    title: "",
    genre: null,
    category: null,
    sortBy: null
  };
};

const handleVote = async (songId) => {
  if (!canVote.value || votingInProgress.value) return;

  votingInProgress.value = true;
  votingSongId.value = songId;

  const startedAt = Date.now();

  try {
    const result = await votesStore.submitVote(event.value.id, songId);
    if (!result.success) {
      console.error("Vote failed:", result.message);
    } else {
      recentlyVotedSongId.value = songId;
      if (recentlyVotedTimeout) clearTimeout(recentlyVotedTimeout);
      recentlyVotedTimeout = setTimeout(() => {
        if (recentlyVotedSongId.value === songId) {
          recentlyVotedSongId.value = null;
        }
        recentlyVotedTimeout = null;
      }, 900);
    }
  } finally {
    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(minimumVoteSpinnerMs - elapsed, 0);
    if (remaining > 0) {
      await new Promise((resolve) => setTimeout(resolve, remaining));
    }
    votingInProgress.value = false;
    votingSongId.value = null;
  }
};

const fetchEvent = async () => {
  try {
    eventPageStore.setLoading(true);
    error.value = null;

    // Fetch event by slug
    const response = await api.get(`/api/events/slug/${route.params.slug}`);

    if (response.data.success) {
      const fetchedEvent = response.data.data.event;
      const fetchedSongs = response.data.data.songs || [];

      event.value = fetchedEvent;
      songs.value = filterSongsForEvent(fetchedSongs, fetchedEvent);
      votingStatus.value = response.data.data.votingStatus;
      votingStartTime.value = response.data.data.votingStartTime;
      votingEndTime.value = response.data.data.votingEndTime;
      eventEndTime.value = response.data.data.eventEndTime;
      resultsAvailableUntil.value = response.data.data.resultsAvailableUntil;
      activeOverlayTemplate.value = response.data.data.activeOverlayTemplate || null;

      // Store setlist settings if provided
      if (response.data.data.setlistSettings) {
        setlistSettings.value = response.data.data.setlistSettings;
      }

      concertsStyleStore.applyEventStyle(event.value.concertsStyle);

      const resolvedLocales = resolveEventLocales(
        fetchedEvent.enabledLocales,
        fetchedEvent.defaultLocale
      );
      eventPageStore.setEventLocales(resolvedLocales.enabledLocales);
      event.value = {
        ...fetchedEvent,
        enabledLocales: resolvedLocales.enabledLocales,
        defaultLocale: resolvedLocales.defaultLocale
      };

      const locale = resolveLandingLocale(
        fetchedEvent.enabledLocales,
        fetchedEvent.defaultLocale,
        route.query.lang
      );
      languageStore.applyEventLocale(locale);

      // Wise always uses dark theme; otherwise honour the event theme setting
      if (!concertsStyleStore.isWise && event.value.theme) {
        themeStore.setEventTheme(event.value.theme);
      }

      // Set default view based on voting status
      if (
        votingStatus.value === "view_only" ||
        votingStatus.value === "ended" ||
        votingStatus.value === "not_started" ||
        votingStatus.value === "disabled"
      ) {
        activeView.value = "results";
      } else {
        activeView.value = "vote";
      }

      // Fetch votes for this event
      await votesStore.fetchEventVotes(event.value.id);

      // Fetch user's votes (which songs this device has voted for)
      await votesStore.fetchUserVotes(event.value.id);

      // Connect socket and join event room
      socketStore.connect();
      socketStore.joinEvent(event.value.id);

      // Initialize deviceId if not already set
      if (!window.deviceId) {
        deviceStore.initDeviceId();
      }
    } else {
      error.value = "Event not found";
    }
  } catch (err) {
    if (err.response?.status === 404) {
      // Use the specific error message from the backend if available
      error.value = err.response?.data?.message || "Event not found or has ended";
    } else {
      error.value = err.response?.data?.message || "Failed to load event";
    }
  } finally {
    eventPageStore.setLoading(false);
  }
};

// Handle voting status changes from WebSocket
const handleOverlayChanged = (data) => {
  if (data.eventId === event.value?.id) {
    activeOverlayTemplate.value = data.overlayTemplate || null;
  }
};

const handleVotingStatusChanged = (data) => {
  if (data.eventId === event.value?.id) {
    // Update voting status based on isVotingDisabled
    if (data.isVotingDisabled) {
      votingStatus.value = "disabled";
    } else {
      // Recalculate voting status based on timing
      const now = new Date();
      const votingStartTime = new Date(event.value.votingStartTime);
      const votingEndTime = event.value.votingEndTime ? new Date(event.value.votingEndTime) : null;
      const eventEndTime = event.value.eventEndTime ? new Date(event.value.eventEndTime) : null;

      const isBeforeStart = now < votingStartTime;
      const isAfterEventEnd = eventEndTime && now > eventEndTime;

      if (isBeforeStart) {
        votingStatus.value = "not_started";
      } else if (isAfterEventEnd) {
        votingStatus.value = "ended";
      } else if (votingEndTime && now > votingEndTime) {
        votingStatus.value = "view_only";
      } else {
        votingStatus.value = "available";
      }
    }

    // Switch to results view if voting is disabled
    if (data.isVotingDisabled && activeView.value === "vote") {
      activeView.value = "results";
    }
  }
};

// Handle set changes from WebSocket (admin advanced to next set)
const handleSetChanged = (data) => {
  if (data.eventId === event.value?.id) {
    event.value = {
      ...event.value,
      currentSetIndex: data.currentSetIndex,
      currentSetStartedAt: data.currentSetStartedAt,
      ...(data.setlistDurations !== undefined ? { setlistDurations: data.setlistDurations } : {}),
      ...(data.setEndSongCount !== undefined ? { setEndSongCount: data.setEndSongCount } : {})
    };
  }
  // Also feed the store: set_changed carries the enriched results payload and
  // draws from the same per-event seq counter as the other broadcasts.
  votesStore.handleSetChanged(data);
};

// Wrapper for song_performed that also updates event set tracking data
const handleSongPerformedWithSetUpdate = async (data) => {
  if (event.value && data.eventId === event.value.id) {
    const next = { ...event.value };
    if (data.currentSetStartedAt !== undefined) {
      next.currentSetStartedAt = data.currentSetStartedAt;
      next.currentSetIndex = data.currentSetIndex ?? event.value.currentSetIndex;
    }
    if (data.setEndSongCount !== undefined) {
      next.setEndSongCount = data.setEndSongCount;
    } else if (event.value.setEndSongCount != null) {
      next.setEndSongCount = Math.max(0, event.value.setEndSongCount - 1);
    }
    event.value = next;
  }
  await votesStore.handleSongPerformed(data);
};

// Quiet event refetch for the realtime safety net (TODO 3.1/3.3).
const refreshEventQuietly = async () => {
  try {
    const data = await fetchEventDataBySlug(route.params.slug);
    if (!data) {
      return;
    }
    event.value = {
      ...event.value,
      ...data.event
    };
    if (Object.prototype.hasOwnProperty.call(data, "activeOverlayTemplate")) {
      activeOverlayTemplate.value = data.activeOverlayTemplate || null;
    }
  } catch (err) {
    console.error("Event quiet refresh failed:", err);
  }
};

let stopRealtimeSafetyNet = null;

onMounted(async () => {
  if (isLedView.value) {
    return;
  }

  await fetchEvent();

  // Ensure socket is connected before setting up listeners
  if (!socketStore.isConnected) {
    socketStore.connect();
  }

  // Listen for vote updates
  socketStore.on("vote_updated", votesStore.handleVoteUpdated);
  socketStore.on("song_performed", handleSongPerformedWithSetUpdate);
  socketStore.on("song_ended", votesStore.handleSongEnded);
  socketStore.on("voting_status_changed", handleVotingStatusChanged);
  socketStore.on("set_changed", handleSetChanged);
  socketStore.on("overlay_changed", handleOverlayChanged);

  // Also listen for connection events to re-setup listeners
  socketStore.on("connect", () => {
    if (event.value) {
      socketStore.joinEvent(event.value.id);
    }
  });

  // Phase 3 — reconnect resync (3.1) + jittered periodic reconciliation (3.3).
  stopRealtimeSafetyNet = useRealtimeSafetyNet({
    getEventId: () => event.value?.id,
    resyncVotes: votesStore.scheduleVotesRefresh,
    socketStore,
    refreshEvent: refreshEventQuietly
  });
  startSetEndSlots();
});

onUnmounted(() => {
  if (recentlyVotedTimeout) clearTimeout(recentlyVotedTimeout);

  stopSetEndSlots();
  stopRealtimeSafetyNet?.();
  stopRealtimeSafetyNet = null;

  socketStore.off("vote_updated", votesStore.handleVoteUpdated);
  socketStore.off("song_performed", handleSongPerformedWithSetUpdate);
  socketStore.off("song_ended", votesStore.handleSongEnded);
  socketStore.off("voting_status_changed", handleVotingStatusChanged);
  socketStore.off("set_changed", handleSetChanged);
  socketStore.off("overlay_changed", handleOverlayChanged);

  // fallow-ignore-next-line code-duplication -- mirrors LedView.vue's unmount
  // cleanup; the two views share no lifecycle composable today (Phase 0 /
  // TODO 0.1, Tasks/12-scalability-and-bottleneck-hardening.md).
  if (event.value) {
    socketStore.leaveEvent(event.value.id);
    votesStore.cancelPendingRefresh(event.value.id);
  }

  concertsStyleStore.resetToDefault();
  languageStore.restoreLocaleAfterEvent();
  eventPageStore.clearEventLocales();
  eventPageStore.setLoading(false);
  socketStore.disconnect();
});

// Method to open instructional modal for direct mode
const openInstructionsModal = (song) => {
  if (event.value && event.value.signUpMode === "direct") {
    selectedSongForModal.value = song;
    showInstructionsModal.value = true;
  }
};

// Method to open QR modal for qr mode
const openQRModal = (song) => {
  if (event.value && event.value.signUpMode === "qr") {
    selectedSongForModal.value = song;
    showQRModal.value = true;
  }
};

// Modal close handlers
const handleInstructionsClose = () => {
  selectedSongForModal.value = null;
};

const handleQRGenerated = (token) => {
  // Token was generated successfully - user will show QR to admin
  console.log("QR token generated:", token);
};

// Handler for when QR modal is closed after showing QR
const handleQRModalClose = (value) => {
  showQRModal.value = value;
  if (!value) {
    selectedSongForModal.value = null;
  }
};

// Handle song clicks for karaoke events - open modals
const handleSongClick = (song) => {
  // For karaoke events, open appropriate modal based on signUpMode
  if (!isKaraoke.value || !event.value) return;

  switch (event.value.signUpMode) {
    case "direct":
      openInstructionsModal(song);
      break;
    case "qr":
      openQRModal(song);
      break;
    // 'none' or other: do nothing
    default:
      break;
  }
};
</script>
<style scoped>
.results-song-item {
  transition:
    transform 0.35s ease,
    opacity 0.35s ease;
}

.results-song-enter-active,
.results-song-leave-active {
  transition:
    opacity 0.3s ease,
    max-height 0.35s ease;
  overflow: hidden;
}

.results-song-enter-from,
.results-song-leave-to {
  opacity: 0;
  max-height: 0;
}

.results-song-move {
  transition: transform 0.45s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.results-song-voted {
  animation: results-vote-highlight 0.75s ease;
}

@keyframes results-vote-highlight {
  0%,
  100% {
    box-shadow: none;
  }

  35% {
    box-shadow:
      0 0 0 3px rgba(20, 184, 166, 0.45),
      0 8px 24px rgba(20, 184, 166, 0.15);
  }
}

.current-song-badge,
.up-next-badge {
  text-align: center;
  font-family:
    "Oswald",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    "Helvetica Neue",
    Arial,
    sans-serif;
}

@media (max-width: 500px) {
  .current-song-badge,
  .up-next-badge {
    max-width: 100px;
  }
}

@media (max-width: 440px) {
  .event-name {
    font-size: 1rem; /* text-base equivalent */
    line-height: 1.5rem;
  }
}
</style>
